import React, { useMemo } from 'react';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
  ScatterChart, Scatter, ZAxis, ComposedChart, RadarChart, Radar, PolarGrid, 
  PolarAngleAxis, PolarRadiusAxis, Treemap
} from 'recharts';
import { Card } from '../components/ui/card';

const COLORS = ['#D4AF37', '#6366F1', '#10B981', '#F59E0B', '#EC4899', '#8B5CF6', '#14B8A6', '#F97316', '#84CC16', '#06B6D4'];

const formatXLabel = (value) => {
  if (!value) return '';
  const str = String(value);
  if (str.length > 12) return str.substring(0, 10) + '..';
  return str;
};

const formatNumber = (value) => {
  if (typeof value !== 'number') return value;
  if (Math.abs(value) >= 1000000) return (value / 1000000).toFixed(1) + 'M';
  if (Math.abs(value) >= 1000) return (value / 1000).toFixed(1) + 'K';
  return Number.isInteger(value) ? value : value.toFixed(2);
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-3 text-xs max-w-xs">
      <p className="font-semibold text-gray-900 mb-1.5 break-words">{label}</p>
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-2 py-0.5">
          <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: entry.color }} />
          <span className="text-gray-600">{entry.name}:</span>
          <span className="font-medium text-gray-900 ml-auto">{formatNumber(entry.value)}</span>
        </div>
      ))}
    </div>
  );
};

const DataVisualization = ({ visualization }) => {
  const data = visualization?.data || [];
  const type = visualization?.type;
  const config = visualization?.config || {};
  const { xKey, yKeys = [], title, colors = COLORS } = config;

  // Calculate tick interval to avoid label overcrowding
  const tickInterval = useMemo(() => {
    if (data.length <= 8) return 0;
    if (data.length <= 15) return 1;
    if (data.length <= 30) return Math.floor(data.length / 10);
    return Math.floor(data.length / 8);
  }, [data.length]);

  if (!visualization || data.length === 0) return null;

  const xAxisProps = {
    dataKey: xKey,
    tick: { fontSize: 10, fill: '#6b7280' },
    tickFormatter: formatXLabel,
    interval: tickInterval,
    angle: data.length > 8 ? -35 : 0,
    textAnchor: data.length > 8 ? 'end' : 'middle',
    height: data.length > 8 ? 60 : 40,
  };

  const yAxisProps = {
    tick: { fontSize: 10, fill: '#6b7280' },
    tickFormatter: formatNumber,
    width: 55,
  };

  const margins = { top: 10, right: 20, left: 10, bottom: data.length > 8 ? 10 : 5 };

  const renderChart = () => {
    switch (type) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data} margin={margins}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis {...xAxisProps} />
              <YAxis {...yAxisProps} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
              {yKeys.map((key, i) => (
                <Bar key={key} dataKey={key} fill={colors[i % colors.length]} radius={[3, 3, 0, 0]} maxBarSize={50} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        );
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data} margin={margins}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis {...xAxisProps} />
              <YAxis {...yAxisProps} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
              {yKeys.map((key, i) => (
                <Line key={key} type="monotone" dataKey={key} stroke={colors[i % colors.length]}
                  strokeWidth={2} dot={data.length <= 20 ? { fill: colors[i % colors.length], r: 3 } : false}
                  activeDot={{ r: 5, strokeWidth: 0 }} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        );
      case 'area':
        return (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={data} margin={margins}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis {...xAxisProps} />
              <YAxis {...yAxisProps} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
              {yKeys.map((key, i) => (
                <Area key={key} type="monotone" dataKey={key} stroke={colors[i % colors.length]}
                  fill={colors[i % colors.length]} fillOpacity={0.15} strokeWidth={2} />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        );
      case 'pie': {
        const pieData = data.map((item) => ({ name: item[xKey], value: item[yKeys[0]] }));
        return (
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" labelLine={false}
                label={({ name, percent }) => `${String(name).substring(0, 15)}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={90} fill="#8884d8" dataKey="value" fontSize={10}>
                {pieData.map((_, i) => (
                  <Cell key={`cell-${i}`} fill={colors[i % colors.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        );
      }
      case 'donut': {
        const donutData = data.map((item) => ({ name: item[xKey], value: item[yKeys[0]] }));
        const total = donutData.reduce((sum, item) => sum + (item.value || 0), 0);
        return (
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={donutData} cx="50%" cy="50%" labelLine={false}
                innerRadius={50} outerRadius={90} fill="#8884d8" dataKey="value" fontSize={10}
                label={({ name, percent }) => `${String(name).substring(0, 12)}: ${(percent * 100).toFixed(0)}%`}>
                {donutData.map((_, i) => (
                  <Cell key={`cell-${i}`} fill={colors[i % colors.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" className="text-lg font-semibold fill-gray-700">
                {formatNumber(total)}
              </text>
            </PieChart>
          </ResponsiveContainer>
        );
      }
      case 'scatter': {
        // Scatter plot for correlation analysis
        const xKey2 = yKeys[0];
        const yKey2 = yKeys[1] || yKeys[0];
        return (
          <ResponsiveContainer width="100%" height={280}>
            <ScatterChart margin={margins}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey={xKey2} name={xKey2} tick={{ fontSize: 10, fill: '#6b7280' }} tickFormatter={formatNumber} />
              <YAxis dataKey={yKey2} name={yKey2} tick={{ fontSize: 10, fill: '#6b7280' }} tickFormatter={formatNumber} width={55} />
              <ZAxis range={[50, 200]} />
              <Tooltip cursor={{ strokeDasharray: '3 3' }} content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Scatter name={`${xKey2} vs ${yKey2}`} data={data} fill={colors[0]}>
                {data.map((_, i) => (
                  <Cell key={`cell-${i}`} fill={colors[i % colors.length]} />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        );
      }
      case 'horizontal_bar':
      case 'hbar': {
        return (
          <ResponsiveContainer width="100%" height={Math.max(280, data.length * 30)}>
            <BarChart data={data} layout="vertical" margin={{ ...margins, left: 80 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={true} vertical={false} />
              <XAxis type="number" tick={{ fontSize: 10, fill: '#6b7280' }} tickFormatter={formatNumber} />
              <YAxis type="category" dataKey={xKey} tick={{ fontSize: 10, fill: '#6b7280' }} width={75} 
                tickFormatter={(v) => String(v).length > 12 ? String(v).substring(0, 10) + '..' : v} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
              {yKeys.map((key, i) => (
                <Bar key={key} dataKey={key} fill={colors[i % colors.length]} radius={[0, 3, 3, 0]} maxBarSize={25} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        );
      }
      case 'stacked_bar':
      case 'stacked': {
        return (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data} margin={margins}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis {...xAxisProps} />
              <YAxis {...yAxisProps} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
              {yKeys.map((key, i) => (
                <Bar key={key} dataKey={key} fill={colors[i % colors.length]} stackId="a" radius={i === yKeys.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        );
      }
      case 'histogram': {
        // Histogram - bars with no gap between them
        return (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data} margin={margins} barCategoryGap={0} barGap={0}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis {...xAxisProps} />
              <YAxis {...yAxisProps} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
              {yKeys.map((key, i) => (
                <Bar key={key} dataKey={key} fill={colors[i % colors.length]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        );
      }
      case 'combo':
      case 'composed': {
        // Combo chart - bars + line
        return (
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={data} margin={margins}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis {...xAxisProps} />
              <YAxis {...yAxisProps} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
              {yKeys.slice(0, -1).map((key, i) => (
                <Bar key={key} dataKey={key} fill={colors[i % colors.length]} radius={[3, 3, 0, 0]} maxBarSize={40} />
              ))}
              {yKeys.length > 1 && (
                <Line type="monotone" dataKey={yKeys[yKeys.length - 1]} stroke={colors[yKeys.length - 1]} 
                  strokeWidth={2} dot={{ fill: colors[yKeys.length - 1], r: 3 }} />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        );
      }
      case 'radar': {
        // Radar/Spider chart for multi-dimensional comparison
        return (
          <ResponsiveContainer width="100%" height={280}>
            <RadarChart cx="50%" cy="50%" outerRadius="70%" data={data}>
              <PolarGrid stroke="#e5e7eb" />
              <PolarAngleAxis dataKey={xKey} tick={{ fontSize: 10, fill: '#6b7280' }} />
              <PolarRadiusAxis tick={{ fontSize: 9, fill: '#9ca3af' }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {yKeys.map((key, i) => (
                <Radar key={key} name={key} dataKey={key} stroke={colors[i % colors.length]} 
                  fill={colors[i % colors.length]} fillOpacity={0.3} strokeWidth={2} />
              ))}
            </RadarChart>
          </ResponsiveContainer>
        );
      }
      case 'treemap': {
        // Treemap for hierarchical data
        const treemapData = data.map((item, i) => ({
          name: item[xKey],
          size: item[yKeys[0]],
          fill: colors[i % colors.length]
        }));
        return (
          <ResponsiveContainer width="100%" height={280}>
            <Treemap data={treemapData} dataKey="size" aspectRatio={4/3} stroke="#fff" fill="#8884d8">
              {treemapData.map((entry, i) => (
                <Cell key={`cell-${i}`} fill={entry.fill} />
              ))}
              <Tooltip content={<CustomTooltip />} />
            </Treemap>
          </ResponsiveContainer>
        );
      }
      default:
        return null;
    }
  };

  return (
    <Card className="mt-4 p-4 bg-white border-gray-200" data-testid="data-visualization">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-2 h-2 rounded-full bg-[#D4AF37]" />
        <h4 className="text-sm font-semibold text-gray-700">{title || 'Data Visualization'}</h4>
        <span className="text-[10px] text-gray-400 ml-auto">{data.length} data points</span>
      </div>
      {renderChart()}
    </Card>
  );
};

export default DataVisualization;
